// Tested in the use-active-circle tests
import {
    createGlobalClassifierIdentifiers,
    createHasPermission,
    DataClass,
    Permission,
} from '@calm/evexia-web-common/dist/circle';
import useAccount from '@rhg/hooks/use-account';
import { useTracking } from '@rhg/libs/analytics';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { CircleAuthContext } from '@rhg/providers/circle-auth';
import {
    getParsedCircleJwt,
    isCircleJwtExpired,
    CIRCLE_JWT_TTL_MS,
    CIRCLE_JWT_EXPIRATION_BUFFER_MS,
} from '@rhg/utils/circle';
import { useCallback, useContext, useEffect, useMemo } from 'react';

export function useGetCircle() {
    const { account } = useAccount();
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (circleId: number) => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.getUsersCirclesRecipientV1({
                userId: account!.userId,
                recipientId: circleId,
            });
            const circle = response.data;
            if (circle.recipient) {
                queryClient.setQueryData(
                    [queryKeys.circleById, circle.recipient.id],
                    circle
                );
            }
            return circle;
        },
    });
    return { getCircle: mutate, getCircleAsync: mutateAsync, ...otherProps };
}

export function useCircle(circleId: number) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { setUserProfile } = useTracking();
    const { account } = useAccount();

    const enabled = !!(isAuthenticated && account?.userId && circleId > 0);
    // 401 handling lives in the shared axios client — any unauthorized
    // response redirects to the logout page. See
    // @rhg/libs/api/client/unauthorized.
    const { data, refetch, isFetched, ...otherProps } = useQuery({
        queryKey: [queryKeys.circleById, circleId],
        queryFn: async () => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.getUsersCirclesRecipientV1({
                userId: account!.userId,
                recipientId: circleId,
            });
            const partnerId = response?.data?.sponsor?.partnerId;
            const parsedJwt = getParsedCircleJwt(
                response.data.authorizationJWT
            );
            await setUserProfile({
                ...(partnerId ? { PartnerID: partnerId } : {}),
                UserSponsoredCountry: parsedJwt?.countryCode?.toUpperCase(),
                IsDependent: response.data.sponsor?.isDependentGroup,
            });
            return response.data;
        },
        enabled,
        retry: false,
        staleTime: CIRCLE_JWT_TTL_MS - CIRCLE_JWT_EXPIRATION_BUFFER_MS,
    });

    const { watchCircle, authorizationJWTs, cohortInfos } =
        useContext(CircleAuthContext);

    /**
     * Register this circleId with the CircleAuthProvider. The provider
     * owns the JWT lifecycle (storage, expiry timers, refresh triggering) via a
     * query cache subscription. This effect only runs when circleId changes —
     * it does NOT re-run when query data updates, eliminating the N-instance
     * thrashing that occurred when setAuthorizationJWT was called per-component.
     */
    useEffect(() => {
        if (circleId <= 0) return;
        return watchCircle(circleId);
    }, [circleId, watchCircle]);

    /**
     * Merges the raw query data with the provider's JWT/cohort state. The
     * provider may hold a more up-to-date JWT (e.g. after a proactive refresh)
     * than what was last returned by the query, so we prefer the context value
     * when available. The provider also masks expired JWTs as `undefined`; the
     * query data is masked the same way until the provider has synced it.
     */
    const circle: typeof data = useMemo(() => {
        if (!data || !data.recipient) return undefined;

        const hasJwtForCircle = Object.keys(authorizationJWTs).includes(
            circleId.toString()
        );
        const hasCohortInfoForCircle = Object.keys(cohortInfos).includes(
            circleId.toString()
        );

        return {
            ...data,
            ...(isCircleJwtExpired(data.authorizationJWT)
                ? { authorizationJWT: undefined, cohortInfo: undefined }
                : {}),
            ...(hasJwtForCircle
                ? { authorizationJWT: authorizationJWTs[circleId] }
                : {}),
            ...(hasCohortInfoForCircle
                ? { cohortInfo: cohortInfos[circleId] }
                : {}),
        };
    }, [data, authorizationJWTs, cohortInfos, circleId]);

    const globalClassifierIdentifiers = useMemo(
        () => createGlobalClassifierIdentifiers(data),
        [data]
    );

    /*
     * Read off the merged `circle`, not the provider's cohort map:
     * the map is seeded from an effect and lags the query by a render,
     * so `isFetched` could be true while permissions read as absent.
     */
    const hasPermission = useCallback(
        (dataClass: DataClass, requiredAccess: Permission | Permission[]) =>
            createHasPermission(circle?.cohortInfo?.permissionDetail?.perms)(
                dataClass,
                requiredAccess
            ),
        [circle]
    );

    /*
     * A fetched circle whose JWT was masked above is waiting on a refresh, and
     * `hasPermission` reads false throughout. A circle that arrived without a
     * JWT never had authorization, so its false is a real denial.
     */
    const isAuthorizationMasked =
        !!circle && !!data?.authorizationJWT && !circle.authorizationJWT;

    return {
        circle,
        /**
         * True while the circle is fetched but its authorization is masked pending
         * a refresh. `isFetched` stays true throughout, so gate on this too before
         * treating a `hasPermission` result as a denial.
         */
        isAuthorizationMasked,
        /**
         * Returns true if the user has permission to perform the operation to the data class.
         * @param dataClass The name of the data class. `keyof CirclePermissionMap`
         * @param requiredAccess The required permission OR array of permissions
         * @example
         * ```tsx
         * export function SomeComponent() {
         *   const { activeCircle, activeCircleHasPermission } = useActiveCircle()
         *   return (
         *     <div>
         *       {activeCircleHasPermission('avatar', 'c') && <button>Upload Avatar</button>}
         *       {activeCircleHasPermission('inbox', 'c') && <button>Compose Message</button>}
         *     </div>
         *   )
         * }
         * ```
         */
        globalClassifierIdentifiers,
        hasPermission,
        isEnabled: enabled,
        isFetched,
        /**
         * True once `hasPermission` is final. A disabled query never fetches, so
         * it is settled at "no permissions" rather than pending. An enabled one
         * stays pending while the fetched circle's authorization is masked.
         */
        isPermissionsSettled: !enabled || (isFetched && !isAuthorizationMasked),
        refetch,
        ...otherProps,
    };
}
